import { Service } from '@/types/service';
import { Card } from '@/components/ui/card';
import { Shield, ShieldAlert, Activity, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ConfidenceSummaryProps {
  services: Service[];
}

export const ConfidenceSummary = ({ services }: ConfidenceSummaryProps) => {
  const highConfidence = services.filter(s => s.confidence === 'high').length;
  const mediumConfidence = services.filter(s => s.confidence === 'medium').length;
  const lowConfidence = services.filter(s => s.confidence === 'low').length;
  
  const apiChecks = services.filter(s => s.checkMethod === 'api').length;
  const htmlChecks = services.filter(s => s.checkMethod === 'html-parse').length;
  const httpChecks = services.filter(s => s.checkMethod === 'http-status').length;
  
  const confidenceScore = services.length > 0 
    ? Math.round(((highConfidence * 100 + mediumConfidence * 60 + lowConfidence * 20) / services.length))
    : 0;

  return (
    <Card className="p-6 bg-gradient-to-br from-card to-card/50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Data Confidence</h3>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <div className="space-y-2 text-sm">
                <p className="font-semibold">How we check service health:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li><strong>API Check (High):</strong> Verified from official status API</li>
                  <li><strong>HTML Parse (Medium):</strong> Parsed from status page content</li>
                  <li><strong>HTTP Status (Low):</strong> Based on HTTP response only</li>
                </ul>
                <p className="pt-2 border-t text-xs text-muted-foreground">
                  Higher confidence means we have more certainty about the actual service health, not just status page availability.
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="space-y-3">
        {/* Overall Confidence Score */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5">
          <span className="text-sm font-medium">Overall Confidence Score</span>
          <span className={cn(
            "text-2xl font-bold",
            confidenceScore >= 70 ? "text-green-600" : 
            confidenceScore >= 40 ? "text-yellow-600" : "text-orange-600"
          )}>
            {confidenceScore}%
          </span>
        </div>

        {/* Confidence Breakdown */}
        <div className="grid grid-cols-3 gap-2">
          <div className="flex flex-col items-center p-2 rounded-lg bg-green-500/10 border border-green-500/20">
            <Shield className="h-4 w-4 text-green-600 mb-1" />
            <div className="text-2xl font-bold text-green-600">{highConfidence}</div>
            <div className="text-xs text-muted-foreground">High</div>
          </div>
          <div className="flex flex-col items-center p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <Shield className="h-4 w-4 text-yellow-600 mb-1" />
            <div className="text-2xl font-bold text-yellow-600">{mediumConfidence}</div>
            <div className="text-xs text-muted-foreground">Medium</div>
          </div>
          <div className="flex flex-col items-center p-2 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <ShieldAlert className="h-4 w-4 text-orange-600 mb-1" />
            <div className="text-2xl font-bold text-orange-600">{lowConfidence}</div>
            <div className="text-xs text-muted-foreground">Low</div>
          </div>
        </div>

        {/* Method Breakdown */}
        <div className="pt-3 border-t">
          <div className="text-xs text-muted-foreground mb-2">Check Methods:</div>
          <div className="flex gap-2 text-xs">
            <div className="flex-1 bg-muted/50 rounded px-2 py-1 text-center">
              <div className="font-semibold text-green-600">{apiChecks}</div>
              <div className="text-muted-foreground">API</div>
            </div>
            <div className="flex-1 bg-muted/50 rounded px-2 py-1 text-center">
              <div className="font-semibold text-yellow-600">{htmlChecks}</div>
              <div className="text-muted-foreground">HTML</div>
            </div>
            <div className="flex-1 bg-muted/50 rounded px-2 py-1 text-center">
              <div className="font-semibold text-orange-600">{httpChecks}</div>
              <div className="text-muted-foreground">HTTP</div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
